// COMPONENT: Cart milestone bar — syncs UK free-delivery and gift thresholds after cart updates.
class HCFsb {
  constructor(fsb = { selector: "#hc-fsb", events: [], placeholder: "(x)" }) {
    this.validateConfig(fsb);
    this.initializeProperties(fsb);
    this.attachCheckoutEvent();
    this.attachEvents();
  }

  validateConfig(fsb) {
    if (!fsb.selector || typeof fsb.selector !== "string") {
      throw new TypeError("Please pass a valid selector");
    }
  }

  initializeProperties(fsb) {
    this.selector = fsb.selector;
    this.fsbElementContainer = document.querySelector(this.selector);
    this.fsbElement = this.fsbElementContainer?.querySelector(".fsb");

    if (!this.fsbElement) {
      throw new TypeError("Freeshipping Bar Element Not Found");
    }

    this.events = fsb.events;
    this.placeholder = fsb.placeholder;
    this.shippingGoal = parseInt(this.fsbElement.dataset.shippingGoal);
    this.maxGoal = parseInt(this.fsbElement.dataset.maxGoal) || this.shippingGoal;
    this.startHeadline = this.fsbElement.dataset.headlineStart;
    this.endHeadline = this.fsbElement.dataset.headlineEnd;
    this.moneyFormat = this.fsbElement.dataset.moneyFormat || "";

    this.middleHeadlines = [];
    this.freeGifts = [];

    for (let i = 1; i <= 3; i++) {
      const goal = parseInt(this.fsbElement.dataset[`freeGift${i}Goal`]);
      const product = parseInt(this.fsbElement.dataset[`freeGift${i}`]);
      const quantity = Math.max(
        parseInt(this.fsbElement.dataset[`freeGift${i}Quantity`]) || 1,
        1
      );
      const title = this.fsbElement.dataset[`freeGiftTitle${i}`];
      const requirement = this.fsbElement.dataset[`freeGiftRequirement${i}`] || "";
      const headline =
        this.fsbElement.dataset[`headlineMiddle${i}`] ||
        "<span class='fsb-headline__line'>You have unlocked FREE delivery</span><span class='fsb-headline__line'>Spend (x) more to unlock (item)</span><span class='fsb-headline__line'>(requirement)</span>";

      if (goal && product && title) {
        this.freeGifts.push({ goal, product, quantity, title, requirement });
        this.middleHeadlines.push(headline);
      }
    }

    this.fsText = this.fsbElement.dataset.fsText;
  }

  async getCart() {
    try {
      const response = await fetch("/cart.js");
      return await response.json();
    } catch (error) {
      console.error("Error fetching cart:", error);
      throw error;
    }
  }

  getBaseTotal(cart) {
    const giftIds = this.freeGifts.map((g) => g.product);
    return cart.items.reduce((sum, item) => {
      if (this.isNonShippingLine(item) || giftIds.includes(item.id)) {
        return sum;
      }
      return sum + item.line_price;
    }, 0);
  }

  isNonShippingLine(item) {
    if (!item) return false;
    if (item.requires_shipping === false) return true;
    if (item.gift_card === true) return true;
    return typeof item.handle === "string" && item.handle.indexOf("gift-card") !== -1;
  }

  async update() {
    try {
      const cart = await this.getCart();
      const baseTotal = this.getBaseTotal(cart);

      let changed = false;

      for (const gift of this.freeGifts) {
        if (this.needsFreeGiftSync(cart, gift, baseTotal)) {
          await this.setFreeGiftQuantity(
            cart,
            gift.product,
            this.getFreeGiftTargetQuantity(gift, baseTotal)
          );
          changed = true;
        }
      }

      if (changed) {
        location.reload();
        return;
      }

      const virtualCart = { ...cart, total_price: baseTotal };
      this.updateProgressBar(virtualCart);
      this.updateHeadline(virtualCart);
    } catch (error) {
      console.error("Error updating FSB:", error);
    }
  }

  updateProgressBar(cart) {
    if (!this.maxGoal || this.maxGoal <= 0) {
      return;
    }
    let progress = (cart.total_price * 100) / this.maxGoal;
    if (progress > 100) progress = 100;
    if (progress < 0) progress = 0;
    this.fsbElement.style.setProperty("--progress", `${progress}%`);
  }

  updateHeadline(cart) {
    const headline = this.fsbElementContainer.querySelector(".fsb-paragraph");
    if (!headline) return;

    if (cart.total_price < this.shippingGoal) {
      const shippingDifference = this.shippingGoal - cart.total_price;
      headline.innerHTML = this.startHeadline
        .replace(
          this.placeholder,
          `<span class="fsb--number">${this.formatMoney(
            shippingDifference
          )}</span>`
        )
        .replace("(goal)", this.fsText);
      return;
    }

    if (this.freeGifts.length === 0) {
      headline.innerHTML = this.endHeadline || "Free delivery reached!";
      return;
    }

    // SONG-DOC: STK-UK-CART-CAPTION-STAGES-20261014 - Match Liquid's one-gift waiting and completed captions without changing reward sync.
    if (this.freeGifts.length === 1) {
      const gift = this.freeGifts[0];
      const difference = Math.max(gift.goal - cart.total_price, 0);
      const template = cart.total_price < gift.goal
        ? this.endHeadline
        : this.middleHeadlines[0];

      headline.innerHTML = template
        .replace(
          this.placeholder,
          `<span class="fsb--number">${this.formatMoney(difference)}</span>`
        )
        .replace("(item)", gift.title)
        .replace("(requirement)", this.formatRequirement(gift.requirement));
      return;
    }

    for (let i = 0; i < this.freeGifts.length; i++) {
      const gift = this.freeGifts[i];
      const difference = gift.goal - cart.total_price;

      if (cart.total_price < gift.goal) {
        headline.innerHTML = this.middleHeadlines[i]
          .replace(
            this.placeholder,
            `<span class="fsb--number">${this.formatMoney(difference)}</span>`
          )
          .replace("(item)", gift.title)
          .replace("(requirement)", this.formatRequirement(gift.requirement));
        return;
      }
    }

    const lastGift = this.freeGifts[this.freeGifts.length - 1];
    if (cart.total_price >= lastGift.goal) {
      headline.innerHTML = this.endHeadline.replace("(item)", lastGift.title);
    }
  }

  formatRequirement(requirement) {
    if (!requirement) return "";
    const punctuation = requirement.trim().endsWith(".") ? "" : ".";
    return (
      requirement.replace(
        /(£\s?\d+(?:\.\d{2})?)/,
        '<span class="fsb--number">$1</span>'
      ) + punctuation
    );
  }

  attachEvents() {
    if (this.events.length > 0) {
      this.events.forEach((event) => {
        document.addEventListener(event, () => this.update(), true);
      });
    }
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = new RegExp("\\{\\{\\s*(\\w+)\\s*\\}\\}");
    const formatString = format || this.moneyFormat;

    const formatWithDelimiters = (
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) => {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
      );
      return dollars + (parts[1] ? decimal + parts[1] : "");
    };

    const moneyFormat = formatString.match(placeholderRegex)?.[1];
    if (!moneyFormat) {
      return `£${formatWithDelimiters(cents, 0)}`;
    }

    const precision = moneyFormat.indexOf("no_decimals") !== -1 ? 0 : 2;
    const thousands = moneyFormat.indexOf("comma_separator") !== -1 ? "." : ",";
    const decimal = moneyFormat.indexOf("comma_separator") !== -1 ? "," : ".";

    return formatString.replace(
      placeholderRegex,
      formatWithDelimiters(cents, precision, thousands, decimal)
    );
  }

  async addFreeGift(product, quantity = 1) {
    const formData = {
      items: [
        {
          id: product,
          quantity,
          properties: {
            _hc_fsb_free_gift: "true",
          },
        },
      ],
    };
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error("Error adding gift product");
      }
    } catch (error) {
      console.error("Error adding gift:", error);
    }
  }

  async changeLineQuantity(lineKey, quantity) {
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      if (!response.ok) {
        throw new Error("Error changing gift quantity");
      }
    } catch (error) {
      console.error("Error changing gift quantity:", error);
    }
  }

  async setFreeGiftQuantity(cart, product, quantity) {
    const giftLines = this.getProductLines(cart, product);

    // SAFETY: Direct cart/add URLs create untagged gift lines, so rebuild all gift-variant lines before allowing checkout.
    if (giftLines.some((line) => !this.isFsbGiftLine(line))) {
      for (const line of giftLines) {
        await this.changeLineQuantity(line.key, 0);
      }

      if (quantity > 0) {
        await this.addFreeGift(product, quantity);
      }
      return;
    }

    if (giftLines.length === 0) {
      if (quantity > 0) {
        await this.addFreeGift(product, quantity);
      }
      return;
    }

    let quantityToKeep = quantity;
    for (const line of giftLines) {
      const lineQuantity = parseInt(line.quantity, 10) || 0;
      const nextQuantity = Math.max(Math.min(lineQuantity, quantityToKeep), 0);
      quantityToKeep = Math.max(quantityToKeep - nextQuantity, 0);

      if (nextQuantity !== lineQuantity) {
        await this.changeLineQuantity(line.key, nextQuantity);
      }
    }

    if (quantityToKeep > 0) {
      await this.addFreeGift(product, quantityToKeep);
    }
  }

  getFreeGiftTargetQuantity(gift, baseTotal) {
    return baseTotal >= gift.goal ? gift.quantity : 0;
  }

  needsFreeGiftSync(cart, gift, baseTotal) {
    const giftLines = this.getProductLines(cart, gift.product);
    const existingQuantity = this.getProductQuantity(cart, gift.product);
    const targetQuantity = this.getFreeGiftTargetQuantity(gift, baseTotal);
    const hasManualGiftLine = giftLines.some((line) => !this.isFsbGiftLine(line));

    return existingQuantity !== targetQuantity || hasManualGiftLine;
  }

  getProductLines(cart, productId) {
    return cart.items.filter((item) => item.id === productId);
  }

  getProductQuantity(cart, productId) {
    return this.getProductLines(cart, productId).reduce((total, item) => {
      return total + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  isFsbGiftLine(item) {
    return item?.properties?._hc_fsb_free_gift === "true";
  }

  attachCheckoutEvent() {
    if (window.Shopify && Shopify.designMode) {
      return;
    }

    if (!this.freeGifts.length) {
      return;
    }

    const checkoutBtn = document.querySelector("[name='checkout']");
    if (checkoutBtn) {
      checkoutBtn.addEventListener(
        "click",
        async (event) => {
          event.preventDefault();
          await this.handleCheckoutProcess(checkoutBtn);
        },
        { capture: true, once: true }
      );
    }
  }

  hasZapietCheckoutManager() {
    return !!(
      document.querySelector(".zapiet-widget-wrapper") ||
      document.querySelector("#zapiet-checkout-methods") ||
      document.querySelector("[data-zapiet-checkout-button]") ||
      document.querySelector("[class*='zapiet-widget']")
    );
  }

  async handleCheckoutProcess(checkoutBtn) {
    const cart = await this.getCart();
    const baseTotal = this.getBaseTotal(cart);
    let changed = false;

    for (const gift of this.freeGifts) {
      if (this.needsFreeGiftSync(cart, gift, baseTotal)) {
        await this.setFreeGiftQuantity(
          cart,
          gift.product,
          this.getFreeGiftTargetQuantity(gift, baseTotal)
        );
        changed = true;
      }
    }

    if (changed) {
      location.reload();
      return;
    }

    if (checkoutBtn.form && typeof checkoutBtn.form.requestSubmit === "function") {
      checkoutBtn.form.requestSubmit(checkoutBtn);
      return;
    }

    checkoutBtn.form.submit();
  }
}
